/* The order record itself — `/sales/orders/:id`'s content, and later a drawer over the list
   (ORD-3) reusing the same component. Takes finished data (`item`, already-loaded) and a `lang`;
   knows nothing about `:id`, the request, or the loading/error states — that is `Page.js`'s job.

   Read-only: the scope excludes create/edit/delete/status changes entirely (1C is the source of
   truth), so unlike every other archetype-B page in this app there is no dirty state and no save
   bar — closer in shape to `/answers/:id` (no sticky command bar) than to a form. This component
   itself still has no rail of its own (`Page.js` puts one around it, `GeoSection`, which the
   drawer never mounts), which is what keeps it usable unmodified inside the narrower drawer too.

   Field shapes come from the ACTUAL `excalibur/orders/{id}/` response, not the written spec, where
   the two disagree (see the module's task notes): `status` has no `status_display`, `order_type`
   and `counterparty` are nested objects, item rows carry `quantity`/`amount` with no `price`, and
   `point`/`supervisor`/`department`/`created`/`modified` are simply absent — none of those are
   rendered here. */

import React from "react";
import { useHistory } from "react-router-dom";
import { Descriptions, Flex, Table, Tag, Typography } from "antd";
import { LinkOutlined, ShoppingCartOutlined, SyncOutlined, WarningOutlined } from "@ant-design/icons";

import { KpiStrip } from "../../component/detail-page";
import { PersonCell, StatusIcon, dash, textOf } from "../../component/table-cells";
import { ORDERS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";

import { parseGeo } from "./GeoSection";
import { STATUS_ICON } from "./parts";

/** items/units, once — the header's KPI strip is the only current reader, but kept as a named
    helper (mirrors `returns/ReturnDetail.js`'s own `itemTotals`) rather than inlined, so a second
    reader doesn't quietly recompute it a different way. */
const itemTotals = item => ({
  itemsCount: item.items?.length || 0,
  unitsCount: (item.items || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
});

/** The page head — entity icon, number, status, order type, the 1C reference, and the KPI strip.
    Its own export because `Page.js` needs it OUTSIDE `dp-grid` (spec/03 archetype B), and because
    the list's record modal reuses it verbatim as the antd `Modal` `title` — one header for the
    record, not a page version and a separate, thinner modal version that drift apart. Icon is
    `ShoppingCartOutlined`, the same glyph `AppNav.js` uses for the "Замовлення" nav row. */
export function OrderDetailHead({ item, lang }) {
  const t = key => translate(ORDERS_PAGE, key, lang);
  const { Icon, tone } = STATUS_ICON[item.status] || STATUS_ICON.new;
  const statusLabel = translate(ORDERS_PAGE, `order_status_${item.status}`, lang) || item.status;
  const { itemsCount, unitsCount } = itemTotals(item);

  return (
    <div className="dp-head dp-head--fluid">
      <div className="dp-head__body">
        <div className="dp-head__top">
          <div className={`dp-head__icon dp-head__icon--${tone}`}>
            <ShoppingCartOutlined />
          </div>
          <div className="dp-head__main">
            <div className="dp-head__titlerow">
              <h1 className="dp-title">{item.name ? textOf(item.name) : `#${item.id}`}</h1>
              {/* The filled pill `visit/parts.js`'s `StatusTag` already established — a `-bg`
                 tint plus a `-700` label plus an icon, never colour alone (spec/01#status-colour). */}
              <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone}`}>
                {statusLabel}
              </Tag>
              {/* Order type is our own classification, not a verdict — a plain bordered pill,
                 same weight as the external reference beside it, neither one a status. */}
              {item.order_type?.name ? <Tag>{textOf(item.order_type.name)}</Tag> : null}
              {/* Deliberately names no system: `external_id` is this record's id in whatever
                 system feeds ours, and which one that is belongs to the deployment, not to the
                 field. The label lives in the lang group, so it is four locales rather than a
                 Cyrillic "1С:" hard-coded into the English, Armenian and Georgian UI too. */}
              {item.external_id ? (
                <Tag icon={<SyncOutlined />}>
                  {t("k_external_id")}: {textOf(item.external_id)}
                </Tag>
              ) : null}
            </div>
            <Typography.Text type="secondary">
              {/* `.join()` stringifies whatever it is handed, so a stray object here would not
                  crash the page — it would silently print "[object Object]" instead. Restricting
                  the join to actual strings is what keeps that from happening quietly. Order type
                  moved into the badge row above, so it is not repeated here. */}
              {[item.datetime ? dayjs(item.datetime).format("DD.MM.YYYY HH:mm") : null, item.counterparty?.name]
                .filter(value => typeof value === "string" && value)
                .join(" · ")}
            </Typography.Text>
          </div>
        </div>
        {/* Always the same FOUR tiles — see `returns/ReturnDetail.js` for why a tile is never
            dropped when its value is missing. */}
        <KpiStrip
          items={[
            { key: "total", label: translate(ORDERS_PAGE, "k_order_total", lang), value: formatMoney(item.order_total) || dash },
            { key: "positions", label: translate(ORDERS_PAGE, "k_positions_units", lang), value: `${itemsCount} / ${unitsCount}` },
            {
              key: "discount",
              label: translate(ORDERS_PAGE, "k_items_discount", lang),
              value: item.items_discount != null ? formatMoney(item.items_discount) : dash,
            },
            {
              key: "delivery",
              label: translate(ORDERS_PAGE, "k_delivery_date", lang),
              value: item.delivery_date ? dayjs(item.delivery_date).format("DD.MM.YYYY") : dash,
            },
          ]}
        />
      </div>
    </div>
  );
}

const OrderDetail = ({ item, lang, showHeader = true }) => {
  const history = useHistory();
  const t = key => translate(ORDERS_PAGE, key, lang);

  const priceTypeLabel = translate(ORDERS_PAGE, `price_type_${item.price_type}`, lang) || item.price_type;
  /* Read here too, not just in `GeoSection` (the page's `dp-rail`, which the drawer never mounts):
     `isFake` is a fraud signal, and a supervisor scanning orders through the drawer has to see it
     without opening the full page. This row stays a plain fact when nothing is wrong and only
     escalates to the icon+red-text pair when something is. */
  const geoPoints = parseGeo(item.tracked_geolocation);
  const geoFakeCount = geoPoints.filter(p => p.isFake).length;

  const itemColumns = [
    {
      key: "name",
      title: t("k_product_name"),
      ellipsis: true,
      render: (value, row) => textOf(row.product?.name),
    },
    { key: "code", title: t("k_product_code"), width: 160, render: (value, row) => textOf(row.product?.code) },
    {
      key: "quantity",
      title: t("k_quantity"),
      width: 110,
      align: "right",
      render: (value, row) => <span className="lp-num">{textOf(row.quantity)}</span>,
    },
    {
      key: "amount",
      title: t("k_amount"),
      width: 140,
      align: "right",
      render: (value, row) => <span className="lp-num">{formatMoney(row.amount)}</span>,
    },
  ];

  return (
    <>
      {showHeader ? <OrderDetailHead item={item} lang={lang} /> : null}

      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_general")}
        </Typography.Text>
        {/* Two columns, row-major (antd fills left-to-right then wraps), mirroring
           `returns/ReturnDetail.js`'s own grid exactly — see that file for why `{xs:1, sm:1,
           md:2}` rather than a bare `column={2}`. Orders carry no `point`/`supervisor` (absent
           from the real endpoint, see the file header), so the pairs are entity/event,
           people/classification, money/date; geolocation and the audit date each get their own
           full-width row — the first because a fraud warning needs room to say so, the second
           because there is no natural partner left once the others are paired off. */}
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} size="small" colon={false} className="dp-desc">
          <Descriptions.Item label={t("k_counterparty")}>
            {item.counterparty ? (
              <Typography.Text>
                {textOf(item.counterparty.name)}
                {item.counterparty.address ? <Typography.Text type="secondary"> · {textOf(item.counterparty.address)}</Typography.Text> : null}
              </Typography.Text>
            ) : (
              dash
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("k_visit")}>
            {item.visit ? (
              <Typography.Link onClick={() => history.push(`/visit/${item.visit}`)}>
                {t("k_visit")} <LinkOutlined />
              </Typography.Link>
            ) : (
              dash
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("k_created_by")}>{item.created_by ? <PersonCell user={item.created_by} strong={false} /> : dash}</Descriptions.Item>
          <Descriptions.Item label={t("k_price_type")}>{textOf(priceTypeLabel)}</Descriptions.Item>
          {/* `discount` is a `PositiveSmallIntegerField` on the model (0-100, default 0) — a plain
             percentage, not a pre-formatted string, so the raw value alone ("10") read as a bare
             count with nothing saying what it counted. */}
          <Descriptions.Item label={t("k_discount")}>{item.discount != null ? `${item.discount}%` : dash}</Descriptions.Item>
          <Descriptions.Item label={t("k_delivery_date")}>
            <span className="dp-nowrap">{item.delivery_date ? dayjs(item.delivery_date).format("DD.MM.YYYY") : dash}</span>
          </Descriptions.Item>
          {/* `span="filled"` rather than `span={2}` — it takes whatever is left of the current
              row, so the row stays full-width if the `column` breakpoints ever change; a literal
              2 would leave a hole the day a third column appears (antd v6 supports the keyword,
              checked in `descriptions/Item.d.ts`). */}
          <Descriptions.Item label={t("k_tracked_geolocation")} span="filled">
            {geoPoints.length === 0 ? (
              dash
            ) : geoFakeCount > 0 ? (
              <Flex align="center" gap={6}>
                <StatusIcon Icon={WarningOutlined} tone="no" label={t("geo_fake_label")} />
                <Typography.Text type="danger">
                  {t("geo_fake_count_label")}: {geoFakeCount} / {geoPoints.length}
                </Typography.Text>
              </Flex>
            ) : (
              <Typography.Text>
                {t("geo_points_count_label")}: {geoPoints.length}
              </Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("k_status_changed")} span="filled">
            <span className="dp-nowrap">{item.status_changed ? dayjs(item.status_changed).format("DD.MM.YYYY HH:mm") : dash}</span>
          </Descriptions.Item>
        </Descriptions>
        {/* A comment carries the reason behind the order, which reads as content, not metadata —
           see `returns/ReturnDetail.js` for the full reasoning; `additional_info` is dropped for
           the same one given there (near-always `{}`, and where it isn't, duplicate metadata). */}
        {item.comments ? (
          <div className="dp-notes">
            <Typography.Text type="secondary" className="dp-note__label">
              {t("k_comment")}
            </Typography.Text>
            <div className="dp-note__box">{textOf(item.comments)}</div>
          </div>
        ) : null}
      </div>

      {/* Totals live as a footer of THIS table, not a separate "Підсумки" card — the same choice
         `returns/ReturnDetail.js` makes and for the same reason: the total READS as part of the
         table (it sits directly under "Сума", the column it sums) instead of a heading that
         merely happens to be nearby. Four rows instead of returns' two, because an order carries
         four real money facts, not one; only the last is emphasized, since "Всього" is the number
         someone opens this card to find. `dp-summary-cell` (`detail-page.scss`, shared with
         `ReturnDetail`'s own summary row) draws the divider as a class — `Table.Summary.Cell`
         does not forward a `style` prop at all, see that file for the full story. */}
      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_items")}
        </Typography.Text>
        <Table
          rowKey="id"
          size="middle"
          columns={itemColumns}
          dataSource={item.items || []}
          pagination={false}
          scroll={{ x: 520 }}
          locale={{ emptyText: t("no_items") }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row style={{ background: "var(--surface-subtle)" }}>
                <Table.Summary.Cell index={0} colSpan={3} className="dp-summary-cell">
                  <Typography.Text type="secondary">{t("k_order_total")}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right" className="dp-summary-cell">
                  <span className="lp-num">{formatMoney(item.order_total)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row style={{ background: "var(--surface-subtle)" }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Typography.Text type="secondary">{t("k_items_discount")}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span className="lp-num">{formatMoney(item.items_discount)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row style={{ background: "var(--surface-subtle)" }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Typography.Text type="secondary">{t("k_shipping_cost")}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span className="lp-num">{formatMoney(item.shipping_cost)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row style={{ background: "var(--surface-subtle)" }}>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Typography.Text strong>{t("k_total")}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  {/* The one deliberately larger number in this card — 16px is `dp-sec-title`'s
                     own step, matching `ReturnDetail`'s own emphasized summary figure. */}
                  <Typography.Text strong className="lp-num" style={{ fontSize: 16 }}>
                    {formatMoney(item.total)}
                  </Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
    </>
  );
};

export default OrderDetail;
